import React, { useMemo, useState } from 'react';
import {
  ArrowLeft, Upload, Search, FolderOpen, HardDrive, AlertTriangle, Loader2, RefreshCw, Lock, Stamp,
} from 'lucide-react';
import { Button } from '../Button';
import { UserProfile } from '../../types';
import { readOnlyMessage, readOnlyTitle } from '../../services/readOnlyNotice';
import { holdIdleExit } from '../../services/idleHold';
import { useRechte } from '../../hooks/useRechte';
import { loescheDokument, setzeAdminSperre, updateDokument, uploadDokument } from '../../services/projektDokumenteService';
import { useDokumente } from './useDokumente';
import { DokumenteListe } from './DokumenteListe';
import { DokumentUploadDialog } from './DokumentUploadDialog';
import { DokumentBearbeitenDialog } from './DokumentBearbeitenDialog';
import { dokumentAnsehen, dokumentHerunterladen } from './dokumentDatei';
import {
  DokumentSortierung, KATEGORIEN, ablaufWarnungen, darfAdminSchloss, darfErzeugteLoeschen, filterDokumente,
  formatBytes, ordnerVorschlaege as ordnerAus, sortiereDokumente, speicherGesamt, zaehleErzeugte,
  zaehleGesperrte, zaehleKategorien,
} from './dokumenteLogic';
import { DokumentEntwurf, DokumentKategorie, ProjektDokument } from './types';

interface Props {
  projectId: string;
  projektName?: string;
  userProfile?: UserProfile | null;
  isReadOnly?: boolean;
  /**
   * Uebungsprojekt (`projects.ist_uebung`): dort darf jeder auch die von der
   * App abgelegten Fassungen loeschen — dieselbe Ausnahme wie in der Policy
   * (Nr. 160/161). Vorgabe `false`.
   */
  istUebungsprojekt?: boolean;
  onBack: () => void;
}

/**
 * Das Dokumentenmanagement eines Projekts — die zentrale Ablage.
 *
 * Hier liegen die FREMDEN Dokumente (Plaene, Vertraege, Genehmigungen,
 * Gefaehrdungsbeurteilungen der Unternehmer, Betriebsanweisungen, Nachweise,
 * Fotos) und seit Stufe 1A auch die EIGENEN Unterlagen: die Fassungen, die
 * Vorankuendigung, SiGe-Plan, A+S-Plan, UfsA, Baustellenordnung/HSE,
 * Handbuch und Einweisung bei Freigabe und Versand ablegen
 * (`services/unterlagenAblage.ts`, Kategorie „Eigene Unterlagen (SiGeKo)").
 *
 * ═══ ZWEI SORTEN ZEILEN, EINE REGEL ═══════════════════════════════════════
 * Eine von der App abgelegte Fassung (`erzeugt_aus`) ist ein Nachweis: sie
 * traegt ein Kennzeichen, laesst sich nur von der Verwaltung loeschen
 * (Policy; der Knopf ist fuer alle anderen ausgegraut und erklaert warum), und
 * das Raeumen beim Archivieren laesst sie stehen. Alles andere ist
 * hochgeladen und gehoert dem Projekt wie bisher.
 *
 * Nicht hier liegen Begehungsberichte (eigenes Archiv) und Rechnungen.
 *
 * Duenner Container: Filtern, Sortieren und Gruppieren stehen als pure
 * Funktionen in dokumenteLogic.ts (dort auch die Tests), das Laden im Hook
 * useDokumente, der Supabase-Zugriff in projektDokumenteService.ts.
 */
export const DokumenteModul: React.FC<Props> = ({
  projectId, projektName, userProfile, isReadOnly = false, istUebungsprojekt = false, onBack,
}) => {
  const { dokumente, laden, fehler, neuLaden, uebernehmen, entfernen } = useDokumente(projectId);

  const [kategorie, setKategorie] = useState<DokumentKategorie | null>(null);
  const [suche, setSuche] = useState('');
  const [nurAblaufend, setNurAblaufend] = useState(false);
  const [sortierung, setSortierung] = useState<DokumentSortierung>('neueste');
  const [uploadOffen, setUploadOffen] = useState(false);
  const [bearbeitet, setBearbeitet] = useState<ProjektDokument | null>(null);
  /** Ordner, die die Liste oeffnen soll — dorthin ist gerade etwas abgelegt oder verschoben worden. */
  const [aufklappen, setAufklappen] = useState<string[]>([]);

  // Der Tag wird EINMAL je Mounten bestimmt und dann durchgereicht. So bleibt
  // die Ablaufbewertung waehrend einer Sitzung stabil, und die pure Logik
  // bekommt ihre Gegenwart von aussen statt sie selbst zu ziehen.
  const heute = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const sichtbar = useMemo(
    () => sortiereDokumente(filterDokumente(dokumente, { suche, kategorie, nurAblaufend }, heute), sortierung),
    [dokumente, suche, kategorie, nurAblaufend, sortierung, heute],
  );

  const zahlen = useMemo(() => zaehleKategorien(dokumente), [dokumente]);
  const warnungen = useMemo(() => ablaufWarnungen(dokumente, heute), [dokumente, heute]);
  const vorschlaege = useMemo(() => ordnerAus(dokumente), [dokumente]);
  const belegt = useMemo(() => speicherGesamt(dokumente), [dokumente]);
  const gesperrt = useMemo(() => zaehleGesperrte(dokumente), [dokumente]);
  const erzeugte = useMemo(() => zaehleErzeugte(dokumente), [dokumente]);

  /**
   * Dieselbe Frage wie die Policy (Nr. 160/161): Verwaltung oder
   * Uebungsprojekt. Steuert den Loeschknopf erzeugter Fassungen und die
   * Vorabpruefung in `loescheDokument` — der Riegel bleibt die Datenbank.
   */
  const darfErzeugte = darfErzeugteLoeschen(userProfile?.role, istUebungsprojekt);

  const readOnlyGrund = readOnlyTitle('Dokumente lassen sich nicht ablegen, ändern oder löschen.');

  /**
   * Dieselbe Frage, die die Datenbank stellt: Admin (`is_admin()`) oder das
   * Recht „„Nur Admin“-Dokumente“ (`hat_recht`, Nr. 154). `useRechte` gibt
   * Admins jedes Recht und faellt ohne Migration auf die Systemrolle zurueck
   * — dann wie bisher nur Admins. Laufen Oberflaeche und Datenbank je
   * auseinander, zeigte die Oberflaeche ein Schloss an, das der Server
   * ablehnt.
   *
   * Es ist eine ANZEIGE-Entscheidung, keine Absicherung: der Riegel ist die
   * Policy aus Migration Nr. 38 (fuer das Recht Nr. 154).
   */
  const rechte = useRechte(userProfile?.role);
  const darfSchloss = darfAdminSchloss(rechte);

  /**
   * ═══ ZWEITE REIHE GEGEN DEN LESEMODUS ═══════════════════════════════════
   * Alle Schreibwege dieses Moduls gehen an `App.handleUpdateReport` VORBEI:
   * eigene Tabelle, eigener Bucket. Es gibt hier also keinen spaeten
   * Wachposten, der eine durchgerutschte Aenderung noch abfaengt — anders als
   * bei allem, was am Projekt-JSON haengt. Der gesperrte Knopf allein genuegt
   * deshalb nicht: `isReadOnly` kann zur Laufzeit kippen (ein Admin uebernimmt
   * die Projektsperre), und ein bereits offener Dialog schriebe weiter.
   *
   * Und nie stillschweigend abbrechen — die Absage nennt Hinweis, Grund und
   * Ausweg (readOnlyNotice.ts).
   */
  const abgelehntWennNurAnsicht = (was: string): boolean => {
    if (!isReadOnly) return false;
    alert(readOnlyMessage(was));
    return true;
  };

  const hochladen = async (datei: File, entwurf: DokumentEntwurf) => {
    if (abgelehntWennNurAnsicht('Die Datei wurde nicht abgelegt.')) return;
    // Ein laufender Upload darf nicht vom Leerlauf-Rueckzug (30 Minuten ohne
    // Eingabe) abgeschnitten werden — beim Ablegen eines Plansatzes klickt
    // niemand nebenbei irgendwo hin.
    const freigeben = holdIdleExit('Dokumenten-Upload läuft');
    try {
      const angelegt = await uploadDokument({
        projectId, datei, dateiname: datei.name, entwurf,
        benutzerId: userProfile?.id,
        benutzerName: [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') || userProfile?.email,
      });
      uebernehmen(angelegt);
      setAufklappen([angelegt.ordner]);
    } finally {
      freigeben();
    }
  };

  const speichern = async (updates: Partial<ProjektDokument>) => {
    if (!bearbeitet) return;
    if (abgelehntWennNurAnsicht('Die Änderungen wurden nicht gespeichert.')) return;
    const geaendert = await updateDokument(bearbeitet.id, updates);
    uebernehmen(geaendert);
    // Wurde das Dokument in einen anderen Ordner verschoben, soll es dort
    // sichtbar sein und nicht in einem zugeklappten Ordner verschwinden.
    if (geaendert.ordner !== bearbeitet.ordner) setAufklappen([geaendert.ordner]);
  };

  /**
   * Schloss setzen oder aufheben.
   *
   * Ohne eigene Rollenpruefung — der Knopf steht nur bei darfSchloss, und die
   * eigentliche Absage kaeme ohnehin vom Server. Der Fehler bleibt bewusst
   * unbehandelt, die Liste zeigt ihn an der Stelle, an der geklickt wurde.
   */
  const sperreUmschalten = async (d: ProjektDokument) => {
    if (abgelehntWennNurAnsicht('Das Admin-Schloss wurde nicht geändert.')) return;
    uebernehmen(await setzeAdminSperre(d.id, !d.nur_admin));
  };

  /**
   * Der Fehler wird hier NICHT abgefangen: die Liste faengt ihn selbst und
   * zeigt ihn oben in ihrem Meldungsband. Ihn zusaetzlich hier anzuzeigen
   * ergaebe dieselbe Meldung zweimal.
   */
  const loeschen = async (d: ProjektDokument) => {
    if (abgelehntWennNurAnsicht('Das Dokument wurde nicht gelöscht.')) return;
    await loescheDokument(d, { darfErzeugteLoeschen: darfErzeugte });
    entfernen(d.id);
  };

  const feld = 'w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm '
    + 'focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';

  return (
    <div data-glass-view="module-dokumente" data-glass-bereich="module" className="h-full flex flex-col bg-slate-50">
      {/* Kopfzeile */}
      <div data-glass="kopf" className="bg-slate-900 text-white p-4 shrink-0 flex items-center justify-between shadow-md z-20">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onBack} className="p-2 rounded-lg hover:bg-white/10 transition-colors shrink-0" title="Zurück zum Projekt">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="font-bold text-lg leading-tight truncate">Dokumente</h1>
            <p className="text-xs text-slate-400 truncate">{projektName || 'Projektablage'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => void neuLaden()} className="p-2 rounded-lg hover:bg-white/10 transition-colors" title="Neu laden">
            <RefreshCw className={`w-4 h-4 ${laden ? 'animate-spin' : ''}`} />
          </button>
          <Button
            data-tutorial="dokumente.ablegen"
            onClick={() => setUploadOffen(true)}
            disabled={isReadOnly}
            title={isReadOnly ? readOnlyGrund : undefined}
            icon={<Upload className="w-4 h-4" />}
          >
            Ablegen
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        {/* Was hier NICHT liegt — einmal ausgesprochen, damit niemand
            Begehungsberichte hier sucht oder die Ablage fuer das Archiv haelt. */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-blue-50 text-blue-600"><FolderOpen className="w-4 h-4" /></div>
            <div>
              <p className="text-sm font-bold text-slate-800">{dokumente.length} {dokumente.length === 1 ? 'Dokument' : 'Dokumente'}</p>
              <p className="text-[11px] text-slate-400">in der Projektablage</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-slate-50 text-slate-500"><HardDrive className="w-4 h-4" /></div>
            <div>
              <p className="text-sm font-bold text-slate-800">{formatBytes(belegt)}</p>
              <p className="text-[11px] text-slate-400">belegter Speicher</p>
            </div>
          </div>
          {/* Nur fuer die Verwaltung sichtbar und nur, wenn es etwas zu melden gibt.
              Die Zahl beantwortet die Frage, die ein Admin sonst nicht stellen
              kann: wie viel von dem, was ich hier sehe, sehen meine Kollegen
              NICHT? */}
          {darfSchloss && gesperrt > 0 && (
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600"><Lock className="w-4 h-4" /></div>
              <div>
                <p className="text-sm font-bold text-slate-800">{gesperrt} mit Schloss</p>
                <p className="text-[11px] text-slate-400">nur für die Verwaltung sichtbar</p>
              </div>
            </div>
          )}
          {/* Wie viele Zeilen die App selbst abgelegt hat — die Zahl sagt,
              wie viel von dieser Ablage Nachweis ist und nicht Handablage. */}
          {erzeugte > 0 && (
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600"><Stamp className="w-4 h-4" /></div>
              <div>
                <p className="text-sm font-bold text-slate-800">{erzeugte} von der App abgelegt</p>
                <p className="text-[11px] text-slate-400">
                  {erzeugte === 1 ? 'Fassung als Nachweis' : 'Fassungen als Nachweis'}
                  {darfErzeugte ? '' : ' — löschen kann sie nur die Verwaltung'}
                </p>
              </div>
            </div>
          )}
          <p className="text-[11px] text-slate-400 flex-1 min-w-[220px]">
            Vorankündigung, SiGe-Plan, A+S-Plan, UfsA, Baustellenordnung, Handbuch und Einweisung legen ihre
            Fassungen bei Freigabe und Versand unter „Eigene Unterlagen (SiGeKo)" ab. Begehungsberichte und
            Rechnungen haben ihre eigenen Module — sie liegen nicht hier und werden von hier aus auch nie gelöscht.
          </p>
        </div>

        {/* Ablaufwarnung */}
        {(warnungen.abgelaufen.length > 0 || warnungen.laufenAb.length > 0) && (
          <button
            onClick={() => { setNurAblaufend(true); setKategorie(null); }}
            className="w-full text-left p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center gap-3 hover:bg-amber-100/70 transition-colors"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">
              {warnungen.abgelaufen.length > 0 && (
                <strong>{warnungen.abgelaufen.length} {warnungen.abgelaufen.length === 1 ? 'Nachweis ist' : 'Nachweise sind'} abgelaufen</strong>
              )}
              {warnungen.abgelaufen.length > 0 && warnungen.laufenAb.length > 0 && ', '}
              {warnungen.laufenAb.length > 0 && <>{warnungen.laufenAb.length} {warnungen.laufenAb.length === 1 ? 'läuft' : 'laufen'} in den nächsten 30 Tagen ab</>}
              . <span className="underline">Anzeigen</span>
            </p>
          </button>
        )}

        {/* Kategorie-Reiter */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setKategorie(null)}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${
              kategorie === null ? 'bg-blue-600 text-white shadow' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            Alle ({dokumente.length})
          </button>
          {KATEGORIEN.filter(k => zahlen[k.id]).map(k => (
            <button
              key={k.id}
              onClick={() => setKategorie(kategorie === k.id ? null : k.id)}
              title={k.hinweis}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${
                kategorie === k.id ? 'bg-blue-600 text-white shadow' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {k.label} ({zahlen[k.id]})
            </button>
          ))}
        </div>

        {/* Suche und Sortierung */}
        <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex gap-2 flex-wrap items-center">
          <div className="relative flex-1 min-w-[220px] group">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              value={suche} onChange={e => setSuche(e.target.value)}
              placeholder="Titel, Dateiname, Ordner, Stand oder Schlagwort…" className={feld}
            />
          </div>
          <select
            value={sortierung} onChange={e => setSortierung(e.target.value as DokumentSortierung)}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500"
          >
            <option value="neueste">Neueste zuerst</option>
            <option value="aelteste">Älteste zuerst</option>
            <option value="name">Name (A–Z)</option>
            <option value="groesse">Größe</option>
            <option value="ablauf">Ablaufdatum</option>
          </select>
          <button
            onClick={() => setNurAblaufend(v => !v)}
            className={`px-3 py-2 rounded-xl text-xs font-bold transition-colors ${
              nurAblaufend ? 'bg-amber-500 text-white shadow' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
            }`}
          >
            Nur ablaufende
          </button>
        </div>

        {fehler && (
          <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-700 whitespace-pre-line">{fehler}</div>
        )}

        {/* Beim Neuladen bleibt die Liste stehen (der Knopf oben dreht sich):
            sonst fiele sie aus dem Baum, und jeder aufgeklappte Ordner waere
            danach wieder zu. Das Rad steht nur, solange noch nichts da ist. */}
        {laden && !dokumente.length ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
        ) : (
          <DokumenteListe
            dokumente={sichtbar}
            heute={heute}
            isReadOnly={isReadOnly}
            readOnlyHinweis={readOnlyGrund}
            zeigeKategorie={kategorie === null}
            filterSchluessel={[suche.trim(), nurAblaufend ? 'ablaufend' : ''].filter(Boolean).join('|')}
            aufklappen={aufklappen}
            onAnsehen={dokumentAnsehen}
            onHerunterladen={dokumentHerunterladen}
            onBearbeiten={setBearbeitet}
            onLoeschen={loeschen}
            darfSchloss={darfSchloss}
            onSperreUmschalten={sperreUmschalten}
            darfErzeugteLoeschen={darfErzeugte}
          />
        )}
      </div>

      {uploadOffen && (
        <DokumentUploadDialog
          ordnerVorschlaege={vorschlaege}
          isReadOnly={isReadOnly}
          darfSchloss={darfSchloss}
          kategorieVorgabe={kategorie || undefined}
          onAbbrechen={() => setUploadOffen(false)}
          onHochladen={hochladen}
        />
      )}

      {bearbeitet && (
        <DokumentBearbeitenDialog
          dokument={bearbeitet}
          ordnerVorschlaege={vorschlaege}
          isReadOnly={isReadOnly}
          onAbbrechen={() => setBearbeitet(null)}
          onSpeichern={speichern}
        />
      )}
    </div>
  );
};
